import { useState, useEffect } from "react";
import { BarChart3, Trophy, Shield } from "lucide-react";
import { getStandings } from "../lib/api";
import { toast } from "sonner";

export default function Standings() {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStandings();
  }, []);

  const fetchStandings = async () => {
    try {
      const response = await getStandings();
      setStandings(response.data);
    } catch (error) {
      toast.error("Failed to fetch standings");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="standings-page" className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="font-heading text-4xl md:text-5xl font-extrabold uppercase tracking-tight">
          League Standings
        </h1>
        <p className="text-muted-foreground mt-2">
          Current tournament rankings
        </p>
      </div>

      {/* Standings Table */}
      {standings.length > 0 ? (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full standings-table">
              <thead>
                <tr>
                  <th className="p-4 text-left sticky left-0 bg-card z-10">
                    #
                  </th>
                  <th className="p-4 text-left sticky left-10 bg-card z-10 min-w-[200px]">
                    Team
                  </th>
                  <th className="p-4 text-center">P</th>
                  <th className="p-4 text-center">W</th>
                  <th className="p-4 text-center">D</th>
                  <th className="p-4 text-center">L</th>
                  <th className="p-4 text-center hide-mobile">GF</th>
                  <th className="p-4 text-center hide-mobile">GA</th>
                  <th className="p-4 text-center">GD</th>
                  <th className="p-4 text-center">PTS</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((team, index) => (
                  <tr
                    key={team.team_id}
                    className={`animate-fade-in opacity-0 ${
                      index < 4 ? "border-l-2 border-l-primary" : ""
                    }`}
                    style={{ animationDelay: `${index * 50}ms` }}
                    data-testid={`standing-row-${team.team_id}`}
                  >
                    <td className="p-4 sticky left-0 bg-card">
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold ${
                          index === 0
                            ? "bg-amber-500/20 text-amber-500"
                            : index < 4
                            ? "bg-primary/20 text-primary"
                            : "bg-secondary text-muted-foreground"
                        }`}
                      >
                        {index + 1}
                      </div>
                    </td>
                    <td className="p-4 sticky left-10 bg-card">
                      <div className="flex items-center gap-3">
                        {team.team_logo ? (
                          <img
                            src={team.team_logo}
                            alt={team.team_name}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                            <Shield className="w-5 h-5 text-muted-foreground" />
                          </div>
                        )}
                        <span className="font-medium">{team.team_name}</span>
                        {index === 0 && (
                          <Trophy className="w-4 h-4 text-amber-500" />
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-center text-muted-foreground">
                      {team.played}
                    </td>
                    <td className="p-4 text-center text-green-500 font-medium">
                      {team.won}
                    </td>
                    <td className="p-4 text-center text-muted-foreground">
                      {team.drawn}
                    </td>
                    <td className="p-4 text-center text-red-500 font-medium">
                      {team.lost}
                    </td>
                    <td className="p-4 text-center text-muted-foreground hide-mobile">
                      {team.goals_for}
                    </td>
                    <td className="p-4 text-center text-muted-foreground hide-mobile">
                      {team.goals_against}
                    </td>
                    <td
                      className={`p-4 text-center font-medium ${
                        team.goal_difference > 0
                          ? "text-green-500"
                          : team.goal_difference < 0
                          ? "text-red-500"
                          : "text-muted-foreground"
                      }`}
                    >
                      {team.goal_difference > 0 ? "+" : ""}
                      {team.goal_difference}
                    </td>
                    <td className="p-4 text-center">
                      <span className="font-heading text-2xl font-black text-primary">
                        {team.points}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="p-4 border-t border-border">
            <div className="flex flex-wrap gap-6 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-primary" />
                <span>Knockout Qualification</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold">P</span> - Played
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold">W</span> - Won
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold">D</span> - Drawn
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold">L</span> - Lost
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold">GF</span> - Goals For
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold">GA</span> - Goals Against
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold">GD</span> - Goal Difference
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold">PTS</span> - Points
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <BarChart3 className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold uppercase mb-2">
            No Standings Yet
          </h3>
          <p className="text-muted-foreground">
            Add teams and complete matches to see standings
          </p>
        </div>
      )}
    </div>
  );
}
